import {
  FieldConfig,
  getBalltrackerByMachineUuidStrikeZoneOptions,
  getBalltrackerByMachineUuidUnifiedQueryKey,
  GetBalltrackerByMachineUuidUnifiedResponse,
  ParkConfig,
  postBalltrackerByMachineUuidUnified,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useUpdateUnifiedConfig = (machineUuid: string) => {
  const queryClient = useQueryClient();
  const queryKey = getBalltrackerByMachineUuidUnifiedQueryKey({
    path: { machine_uuid: machineUuid },
  });

  return useMutation({
    mutationFn: (patch: Partial<ParkConfig>) =>
      postBalltrackerByMachineUuidUnified({
        path: { machine_uuid: machineUuid },
        body: patch,
        throwOnError: true,
      }).then(({ data }) => data),

    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey });

      const snapshot =
        queryClient.getQueryData<GetBalltrackerByMachineUuidUnifiedResponse>(
          queryKey,
        );

      queryClient.setQueryData<GetBalltrackerByMachineUuidUnifiedResponse>(
        queryKey,
        (cached) => {
          if (!cached || !("body" in cached) || typeof cached.body !== "object")
            return cached;

          const next = { ...cached.body };

          if (patch.sport != null) next.sport = patch.sport;
          if (patch.rubberDistanceF != null)
            next.rubberDistanceF = patch.rubberDistanceF;
          if (patch.strikeZone != null) next.strikeZone = patch.strikeZone;
          if (patch.field !== undefined) {
            next.field = {
              ...(cached.body.field ?? {}),
              ...patch.field,
            } as FieldConfig;
          }

          return { ...cached, body: next };
        },
      );

      return { snapshot };
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(queryKey, context.snapshot);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useStrikeZoneConfig = (machineUuid: string) => {
  return useQuery({
    ...getBalltrackerByMachineUuidStrikeZoneOptions({
      path: { machine_uuid: machineUuid },
    }),
    enabled: !!machineUuid,
    retry: false,
    select: (response) => {
      const maybeNested = response as unknown as {
        body?: { strikeZone?: (typeof response)["strikeZone"] };
      };
      return response?.strikeZone ?? maybeNested?.body?.strikeZone ?? null;
    },
  });
};
